/**
 * Task intake write helpers (T-039 / T-040).
 *
 * The shape-check LLM call is server-side only per ARCH-009 (POST
 * /api/v1/subscriber/tasks/shape-check). That endpoint and its studio-mcp
 * helper live OUTSIDE Phase G file ownership (server/ + packages/studio-mcp/
 * are consume-only) and T-039 is an ADR candidate (ARCH-009 is still
 * `proposed`, rules deferred). So this module:
 *   - calls the documented endpoint with the ARCH-009 ShapeCheckRequest shape
 *   - implements the ARCH-009 §5 fail-open behavior (provider/endpoint
 *     unavailable -> soft_warning, subscriber is never blocked)
 *   - writes the final task row via supabase-js (ARCH-001 no ORM). The
 *     subscriber `tasks_subscriber_insert` RLS policy permits the insert; the
 *     row is created with status='backlog' (or 'split' + children).
 *
 * If the shape-check endpoint does not exist yet (404), the helper returns a
 * soft_warning verdict per the ARCH-009 fallback contract — exactly the
 * "shape-check temporarily unavailable, task goes straight to backlog" path.
 */

import { supabase } from '../supabase.js';
import type { CoachingDepth, TaskType } from '@studio/api-schemas';

export interface ShapeCheckRequest {
  brief: string;
  task_type: TaskType;
  coaching_depth: CoachingDepth;
  tier: 'studio' | 'pro';
}

export interface ShapeCheckRisk {
  kind: 'scope' | 'ambiguity' | 'missing_context' | 'out_of_tier_cycle';
  message: string;
}

export interface ShapeCheckProposedChild {
  title: string;
  brief: string;
  task_type: TaskType;
}

export interface ShapeCheckResponse {
  verdict: 'ok' | 'tighten' | 'split' | 'soft_warning';
  tightened_brief: string | null;
  flagged_risks: ShapeCheckRisk[];
  proposed_split: ShapeCheckProposedChild[] | null;
  model: string;
  prompt_version: string;
  latency_ms: number;
}

const SOFT_WARNING_FALLBACK: ShapeCheckResponse = {
  verdict: 'soft_warning',
  tightened_brief: null,
  flagged_risks: [
    {
      kind: 'missing_context',
      message:
        'Shape-check is temporarily unavailable. Your task will go straight to the backlog.',
    },
  ],
  proposed_split: null,
  model: 'unavailable',
  prompt_version: 'unavailable',
  latency_ms: 0,
};

/**
 * Run the server-side shape-check. Fails open to soft_warning per ARCH-009 §5
 * on any non-200, network error, or missing endpoint. Never throws — the
 * caller treats the verdict, never an exception.
 */
export async function runShapeCheck(
  accessToken: string,
  req: ShapeCheckRequest,
): Promise<ShapeCheckResponse> {
  try {
    const res = await fetch('/api/v1/subscriber/tasks/shape-check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(req),
    });
    if (res.status !== 200) return SOFT_WARNING_FALLBACK;
    const json = (await res.json()) as unknown;
    if (
      typeof json === 'object' &&
      json !== null &&
      'verdict' in json &&
      typeof (json as { verdict: unknown }).verdict === 'string'
    ) {
      return json as ShapeCheckResponse;
    }
    return SOFT_WARNING_FALLBACK;
  } catch {
    return SOFT_WARNING_FALLBACK;
  }
}

/**
 * Write a single task to the backlog. RLS `tasks_subscriber_insert` checks
 * `is_owner(subscriber_id)`; we supply subscriber_id (resolved from the
 * caller's own subscriber row by the page) so the WITH CHECK passes. status
 * is 'backlog' (intake confirmed; shape-check is not a hard gate per ARCH-009
 * §6.2 DON'T).
 */
export async function writeBacklogTask(input: {
  subscriberId: string;
  brief: string;
  type: TaskType;
  intakeMode: 'form' | 'chat';
  coachingDepth: CoachingDepth;
}): Promise<{ id: string }> {
  const { data, error } = await supabase
    .from('tasks')
    .insert({
      subscriber_id: input.subscriberId,
      brief: input.brief,
      type: input.type,
      status: 'backlog',
      intake_mode: input.intakeMode,
      coaching_depth: input.coachingDepth,
    })
    .select('id')
    .single();
  if (error) throw error;
  return data as { id: string };
}

/**
 * Write a parent task as `split` plus its child backlog tasks. PR-12 / T-039:
 * subscriber accepted the proposed split into 2-4 children. The parent row
 * carries the original brief and status='split' (terminal per the task status
 * transition table); each child is a normal backlog task referencing the
 * parent via parent_task_id.
 */
export async function writeSplitTasks(input: {
  subscriberId: string;
  parentBrief: string;
  parentType: TaskType;
  intakeMode: 'form' | 'chat';
  coachingDepth: CoachingDepth;
  children: ShapeCheckProposedChild[];
}): Promise<{ parentId: string; childIds: string[] }> {
  const { data: parent, error: parentErr } = await supabase
    .from('tasks')
    .insert({
      subscriber_id: input.subscriberId,
      brief: input.parentBrief,
      type: input.parentType,
      status: 'split',
      intake_mode: input.intakeMode,
      coaching_depth: input.coachingDepth,
    })
    .select('id')
    .single();
  if (parentErr) throw parentErr;
  const parentId = (parent as { id: string }).id;

  const childRows = input.children.map((c) => ({
    subscriber_id: input.subscriberId,
    parent_task_id: parentId,
    brief: c.brief,
    type: c.task_type,
    status: 'backlog' as const,
    intake_mode: input.intakeMode,
    coaching_depth: input.coachingDepth,
  }));
  const { data: kids, error: kidsErr } = await supabase
    .from('tasks')
    .insert(childRows)
    .select('id');
  if (kidsErr) throw kidsErr;
  return {
    parentId,
    childIds: ((kids as { id: string }[]) ?? []).map((k) => k.id),
  };
}
